/**
 * HTML utility functions
 * Pure functions for HTML escaping and sanitization
 * No dependencies
 */

const URL_REGEX = /(\b(?:https?|ftp):\/\/[^\s<>"]+|#(?:project-\d+|task-\d+|tasks\?task=[^\s<>"]+|docs\?doc=[^\s<>"]+))/gi;

function normalizeTextLineBreaks(text) {
    return String(text || "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n");
}

function parseHashAndQuery(rawHash = "") {
    const hashAndQuery = String(rawHash || "");
    const queryIndex = hashAndQuery.indexOf("?");
    if (queryIndex === -1) {
        return {
            hash: hashAndQuery,
            searchParams: null
        };
    }

    return {
        hash: hashAndQuery.slice(0, queryIndex),
        searchParams: new URLSearchParams(hashAndQuery.slice(queryIndex + 1))
    };
}

function getAppOrigin(context = {}) {
    if (context.appOrigin) return context.appOrigin;
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin;
    }
    return "";
}

function getBaseHref(context = {}) {
    if (context.baseHref) return context.baseHref;
    if (typeof window !== "undefined" && window.location?.href) {
        return window.location.href;
    }
    const origin = getAppOrigin(context);
    return origin || "https://localhost";
}

function getTranslator(context = {}) {
    if (typeof context.t === "function") return context.t;
    return (key, fallbackVars) => {
        if (typeof fallbackVars === "string") return fallbackVars;
        return key;
    };
}

function findProjectById(context, id) {
    if (typeof context.findProjectById === "function") {
        return context.findProjectById(id);
    }
    const projectList = Array.isArray(context.projects) ? context.projects : [];
    return projectList.find((project) => Number(project?.id) === Number(id)) || null;
}

function findTaskById(context, id) {
    if (typeof context.findTaskById === "function") {
        return context.findTaskById(id);
    }
    const taskList = Array.isArray(context.tasks) ? context.tasks : [];
    return taskList.find((task) => Number(task?.id) === Number(id)) || null;
}

function findDocById(context, id) {
    if (typeof context.findDocById === "function") {
        return context.findDocById(id);
    }
    const docList = Array.isArray(context.docs) ? context.docs : [];
    return docList.find((doc) => String(doc?.id) === String(id)) || null;
}

function buildExternalDisplayLabel(url) {
    let displayHost = url.hostname.replace(/^www\./i, "");
    if (displayHost.length > 25) {
        displayHost = `${displayHost.slice(0, 22)}...`;
    }

    let displayPath = url.pathname || "";
    if (displayPath === "/") displayPath = "";
    if (displayPath.length > 24) {
        displayPath = `${displayPath.slice(0, 21)}...`;
    }

    return `${displayHost}${displayPath}`;
}

function formatSlugFallback(slug) {
    const rawSlug = String(slug || "").trim();
    if (!rawSlug) return "";

    return rawSlug
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (match) => match.toUpperCase());
}

function buildPresentation(variant, overrides = {}) {
    const className = `docs-internal-link docs-internal-link--${variant}`;
    return {
        variant,
        className,
        ...overrides
    };
}

export function resolveLinkPresentation(href, context = {}) {
    const rawHref = String(href || "").trim();
    if (!rawHref) return null;

    const translate = getTranslator(context);
    const appOrigin = getAppOrigin(context);
    let parsedUrl = null;
    let hash = "";
    let searchParams = null;
    let isInternalAppUrl = false;

    if (rawHref.startsWith("#")) {
        const parsedHash = parseHashAndQuery(rawHref);
        hash = parsedHash.hash;
        searchParams = parsedHash.searchParams;
    } else {
        try {
            parsedUrl = new URL(rawHref, getBaseHref(context));
            const parsedHash = parseHashAndQuery(parsedUrl.hash || "");
            hash = parsedHash.hash;
            searchParams = parsedHash.searchParams;
            if (!searchParams && parsedUrl.search) {
                searchParams = new URLSearchParams(parsedUrl.search);
            }
            isInternalAppUrl = !!appOrigin && parsedUrl.origin === appOrigin;
        } catch (error) {
            return null;
        }
    }

    const projectMatch = hash.match(/^#project-(\d+)$/);
    if (projectMatch) {
        const projectId = Number(projectMatch[1]);
        const project = findProjectById(context, projectId);
        if (!project) return null;

        return buildPresentation("project", {
            href: `#project-${projectId}`,
            displayLabel: project.name || translate("projects.untitled", "Untitled project"),
            target: "_blank",
            rel: "noopener",
            isInternal: true,
            entityId: projectId
        });
    }

    const taskQueryId = hash === "#tasks" && searchParams ? searchParams.get("task") : null;
    const taskMatch = hash.match(/^#task-(\d+)$/);
    const resolvedTaskId = taskMatch ? Number(taskMatch[1]) : Number(taskQueryId);
    if (Number.isFinite(resolvedTaskId) && resolvedTaskId > 0) {
        const task = findTaskById(context, resolvedTaskId);
        if (!task) return null;

        return buildPresentation("task", {
            href: `#task-${resolvedTaskId}`,
            displayLabel: `#${resolvedTaskId} ${task.title || translate("tasks.untitled", "Untitled task")}`,
            dataAction: "openTaskDetails",
            dataParam: String(resolvedTaskId),
            isInternal: true,
            entityId: resolvedTaskId
        });
    }

    const docId = hash === "#docs" && searchParams ? searchParams.get("doc") : null;
    if (docId) {
        const doc = findDocById(context, docId);
        const slugLabel = formatSlugFallback(searchParams?.get("slug"));
        const docLabel = doc?.title || slugLabel || translate("docs.titlePlaceholder", "Untitled page");
        const icon = doc?.icon ? `${doc.icon} ` : "";

        return buildPresentation("doc", {
            href: `#docs?doc=${encodeURIComponent(docId)}${searchParams?.get("slug") ? `&slug=${encodeURIComponent(searchParams.get("slug"))}` : ""}`,
            displayLabel: `${icon}${docLabel}`,
            isInternal: true,
            entityId: String(docId)
        });
    }

    // Filtered lists inside the app (e.g. the status report's "Open in Tasks/Projects"):
    // keep the author's link text instead of replacing it with the host name.
    const listParam = hash === "#tasks" ? "taskIds" : hash === "#projects" ? "projectIds" : "";
    if (listParam && searchParams?.get(listParam) && (isInternalAppUrl || rawHref.startsWith("#"))) {
        return buildPresentation("list", {
            href: `${hash}?${searchParams.toString()}`,
            displayLabel: "",
            target: "_blank",
            rel: "noopener",
            isInternal: true
        });
    }

    try {
        const url = parsedUrl || new URL(rawHref, getBaseHref(context));
        const normalizedHref = isInternalAppUrl && hash ? `${hash}${searchParams ? `?${searchParams.toString()}` : ""}` : url.toString();

        return buildPresentation("external", {
            href: normalizedHref,
            displayLabel: buildExternalDisplayLabel(url),
            target: "_blank",
            rel: "noopener noreferrer nofollow",
            isInternal: isInternalAppUrl
        });
    } catch (error) {
        return null;
    }
}

function renderPresentationAttributes(presentation) {
    const attributes = [
        `href="${escapeHtml(presentation.href || "")}"`,
        `class="${escapeHtml(presentation.className || "")}"`
    ];

    if (presentation.target) {
        attributes.push(`target="${escapeHtml(presentation.target)}"`);
    }
    if (presentation.rel) {
        attributes.push(`rel="${escapeHtml(presentation.rel)}"`);
    }
    if (presentation.dataAction) {
        attributes.push(`data-action="${escapeHtml(presentation.dataAction)}"`);
    }
    if (presentation.dataParam) {
        attributes.push(`data-param="${escapeHtml(presentation.dataParam)}"`);
    }

    return attributes.join(" ");
}

function renderPlainTextChunk(text) {
    return escapeHtml(normalizeTextLineBreaks(text)).replace(/\n/g, "<br>");
}

function renderFallbackAnchor(url) {
    const safeUrl = escapeHtml(url);
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a>`;
}

function normalizeSingleLinePreviewText(text) {
    return normalizeTextLineBreaks(text)
        .replace(/\s+/g, " ")
        .trim();
}

export function renderPlainTextWithBeautifiedLinks(text, context = {}) {
    const source = normalizeTextLineBreaks(text);
    if (!source) return "";

    let html = "";
    let lastIndex = 0;
    URL_REGEX.lastIndex = 0;

    let match = URL_REGEX.exec(source);
    while (match) {
        const url = match[0];
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
            html += renderPlainTextChunk(source.slice(lastIndex, matchIndex));
        }

        const presentation = resolveLinkPresentation(url, context);
        if (presentation) {
            html += `<a ${renderPresentationAttributes(presentation)}>${escapeHtml(presentation.displayLabel || url)}</a>`;
        } else {
            html += renderFallbackAnchor(url);
        }

        lastIndex = matchIndex + url.length;
        match = URL_REGEX.exec(source);
    }

    if (lastIndex < source.length) {
        html += renderPlainTextChunk(source.slice(lastIndex));
    }

    return html;
}

export function renderSingleLinePreviewWithBeautifiedLinks(text, context = {}) {
    const source = normalizeSingleLinePreviewText(text);
    if (!source) return "";
    return renderPlainTextWithBeautifiedLinks(source, context);
}

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for HTML insertion
 *
 * @example
 * escapeHtml('<script>alert("xss")</script>')
 * // Returns: '&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;'
 */
export function escapeHtml(str) {
    return (str || "").replace(
        /[&<>"']/g,
        (m) =>
            ({
                "&": "&amp;",
                "<": "&lt;",
                ">": "&gt;",
                '"': "&quot;",
                "'": "&#39;",
            }[m])
    );
}

/**
 * Convert URLs in text to clickable hyperlinks
 * @param {string} text - Text that may contain URLs
 * @returns {string} Text with URLs converted to <a> tags
 *
 * @example
 * convertUrlsToLinks('Check out https://example.com')
 * // Returns: 'Check out <a href="https://example.com" target="_blank" rel="noopener noreferrer">https://example.com</a>'
 */
export function convertUrlsToLinks(text) {
    if (!text) return '';
    
    // Regex to match URLs (http, https, ftp protocols)
    const urlRegex = /(\b(https?|ftp):\/\/[^\s<>"]+)/gi;
    
    return text.replace(urlRegex, (url) => {
        const safeUrl = escapeHtml(url);
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a>`;
    });
}

/**
 * Escape HTML and convert URLs to clickable links
 * Use this for rendering user-generated content that should display clickable URLs
 * @param {string} str - String to process
 * @returns {string} Processed string safe for HTML insertion with clickable links
 *
 * @example
 * escapeHtmlWithLinks('Visit https://example.com for more info')
 * // Returns: 'Visit <a href="https://example.com"...>https://example.com</a> for more info'
 */
export function escapeHtmlWithLinks(str) {
    if (!str) return '';
    
    // First escape HTML to prevent XSS
    const escaped = escapeHtml(str);
    
    // Then convert URLs to links (the escaped version won't have HTML chars that break the regex)
    return convertUrlsToLinks(escaped);
}

/**
 * Sanitize user input by escaping HTML and trimming whitespace
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized string
 *
 * @example
 * sanitizeInput('  <b>Hello</b>  ')
 * // Returns: '&lt;b&gt;Hello&lt;/b&gt;'
 */
export function sanitizeInput(input) {
    if (!input) return '';
    return escapeHtml(String(input).trim());
}
